// src/layouts/DashboardLayout.tsx
import type { PlanType } from "@/types/subscription.types";
import { fetchTourById } from "@a/tours";
import { fetchAdminUserDetail } from "@a/admin";
import DemoResetChip from "@c/dashboard/DemoResetChip";
import { AppSidebar } from "@c/layout/AppSidebar";
import { HealthBell } from "@c/notifications/HealthBell";
import { NotificationBell } from "@c/notifications/NotificationBell";
import LangDropdown from "@c/shared/LangDropdown";
import { SkipLink } from "@c/shared/SkipLink";
import ThemeToggleButton from "@c/shared/ThemeToggleButton";
import { AccountProvider } from "@ctx/AccountContext";
import { DemoModeProvider } from "@ctx/DemoModeContext";
import {
  HeaderActionsProvider,
  useHeaderActionsSlot,
} from "@ctx/HeaderActionsContext";
import { useAuth } from "@h/useCtx";
import { useQuery } from "@tanstack/react-query";
import { buildRoute } from "@u/routes";
import { SidebarInset, SidebarProvider, SidebarTrigger } from "@ui/sidebar";
import { Tooltip, TooltipContent, TooltipTrigger } from "@ui/tooltip";
import { Play } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, Outlet, useLocation } from "react-router-dom";

type HeaderInnerProps = {
  editTour?: {
    id: string;
    short_id: string;
    title?: string;
    owner_plan_type?: PlanType;
  } | null;
  userName?: string | null;
};

/** Header "view public page" link. Icon-only below sm, so it carries a tooltip
 *  (dropping downward like every other header control). */
function ViewLink({ to }: { to: string }) {
  const { t } = useTranslation();
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Link
          to={to}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={t("view")}
          className="flex items-center gap-1.5 px-3 h-8 rounded-lg text-xs font-mono border border-border bg-primary-strong text-primary-foreground transition"
        >
          <Play size={12} />
          <span className="hidden sm:inline">{t("view")}</span>
        </Link>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="sm:hidden">
        {t("view")}
      </TooltipContent>
    </Tooltip>
  );
}

function HeaderInner({ editTour, userName }: HeaderInnerProps) {
  const headerActions = useHeaderActionsSlot();
  const { t } = useTranslation();

  return (
    <header className="sticky top-0 z-40 w-full flex h-16 shrink-0 items-center gap-2 border-b border-border px-4 bg-background/80 backdrop-blur-md transition-colors duration-500">
      <SidebarTrigger className="-ml-1" />
      <div className="h-4 w-px bg-border mx-2" />

      <div className="flex-1 text-bubbly-sm text-xs md:text-sm tracking-[0.2em] text-muted-foreground truncate hidden sm:block">
        {editTour?.title ?? userName ?? t("control_panel")}
      </div>

      <div className="flex min-w-0 items-center gap-1 md:gap-2 ml-auto overflow-x-auto no-scrollbar">
        {headerActions && (
          <div className="flex items-center gap-1 md:gap-2">
            {headerActions}
          </div>
        )}
        <div className="flex items-center gap-1 md:gap-2 pr-2">
          <DemoResetChip />
          {editTour && <ViewLink to={buildRoute.viewer(editTour.short_id)} />}
          <HealthBell />
          <NotificationBell />
          <LangDropdown design="ghost" />
          <ThemeToggleButton design="ghost" />
        </div>
      </div>
    </header>
  );
}

function DashboardLayoutInner() {
  const location = useLocation();

  // Canonical: /dashboard/tours/:id. Exclude the transient legacy
  // /dashboard/tours/edit/:id (redirects to the canonical path).
  const editMatch = location.pathname.match(
    /^\/dashboard\/tours\/(?!edit(?:\/|$))([^/]+)/,
  );
  const editTourId = editMatch?.[1];

  // Admin user-detail: swap the header title for the user's name.
  const userMatch = location.pathname.match(/^\/dashboard\/users\/([^/]+)/);
  const userDetailId = userMatch?.[1];

  const { data: editTour } = useQuery({
    queryKey: ["tour", editTourId],
    queryFn: () => fetchTourById(editTourId!),
    enabled: !!editTourId,
    staleTime: 5 * 60 * 1000,
  });

  // Same query key as the detail page → dedupes, no extra fetch.
  const { data: detailUser } = useQuery({
    queryKey: ["admin", "users", "detail", userDetailId],
    queryFn: () => fetchAdminUserDetail(userDetailId!),
    enabled: !!userDetailId,
    staleTime: 5 * 60 * 1000,
  });
  const userName = detailUser?.name ?? detailUser?.email ?? null;

  return (
    <AccountProvider>
      <SidebarProvider>
        <SkipLink />
        <AppSidebar />
        <SidebarInset className="flex flex-col min-h-dvh bg-background min-w-0">
          <HeaderActionsProvider>
            <HeaderInner editTour={editTour} userName={userName} />

            <main
              id="main-content"
              tabIndex={-1}
              className="flex-1 p-4 md:p-6 lg:p-8 w-full max-w-screen-2xl mx-auto isolate min-w-0 focus:outline-none"
            >
              <Outlet />
            </main>

            <footer className="w-full py-4 px-6 border-t border-border mt-auto flex items-center justify-between text-xs text-muted-foreground">
              <p>&copy; {new Date().getFullYear()} ANDAR</p>
              <div className="flex gap-4">
                <a
                  href="mailto:support@andarlabs.com"
                  className="hover:text-foreground"
                >
                  Soporte
                </a>
                <Link to="/terms" className="hover:text-foreground">
                  Términos
                </Link>
              </div>
            </footer>
          </HeaderActionsProvider>
        </SidebarInset>
      </SidebarProvider>
    </AccountProvider>
  );
}

export default function DashboardLayout() {
  const { profile } = useAuth();

  // Demo mode is for demo accounts only (`is_demo`). Admins see real data and
  // pass all gating; they don't get the plan-simulation badge.
  if (profile?.is_demo === true) {
    return (
      <DemoModeProvider>
        <DashboardLayoutInner />
      </DemoModeProvider>
    );
  }

  return <DashboardLayoutInner />;
}
